import { GlyphAtlas } from "../render/GlyphAtlas.js";
import type { GpuContext, GpuContextOptions } from "../../engine/GpuContext.js";
import type { ImageStore } from "../../engine/ImageStore.js";
import { Game, type GameLayer } from "../../engine/Game.js";
import type { InputClaimant } from "../../engine/InputRouter.js";
import { QuadBatcher } from "../render/QuadBatcher.js";
import { RenderContext } from "../render/RenderContext.js";
import type { CaptureOptions, CapturedFrame } from "../../engine/capture.js";
import type { Theme } from "../themes/Theme.js";
import { Container } from "./Container.js";
import type { Control } from "./Control.js";
import {
  makeKeyArgs,
  makeMouseArgs,
  type DragPayload,
  type KeyEventArgs,
  type MouseSource,
  type TextInputArgs,
} from "./events.js";
import { DragSession, type DragOptions } from "./dragdrop.js";
import { GamepadReader, isGamepadCode, type GamepadReaderOptions } from "./gamepad.js";
import { FocusManager, type FocusDirection } from "./focus.js";
import { shouldSuppressKey } from "./keys.js";
import { TooltipManager } from "./Tooltip.js";
import type { Insets, Point, Rect } from "../../common/geometry.js";
import { InputManager } from "./input.js";
import { KeyboardBridge, textEntryFor, type TextEntryField } from "./keyboard.js";
import { AutofillForm, type AutofillFieldSpec } from "./autofill.js";
import { AriaMirror } from "../a11y/AriaMirror.js";
import { NavigationIndex } from "../controls/NavigationIndex.js";
import { fitInWorkArea } from "../controls/MenuPopup.js";
import { registerAccessibleDescribers } from "../a11y/describers.js";
import { DEFAULT_ITEM_CAP } from "../a11y/snapshot.js";
import { tileMinimizedStrip } from "./minimizeStrip.js";
import { cascadeRects, tileRects, type TileOrientation } from "./arrange.js";
import { windowCommandFor, type WindowKeyMap } from "./windowKeys.js";

/**
 * Structural contract the Desktop needs from a top-level window, declared here
 * so core does not have to import the Window control.
 */
export interface DesktopWindow extends Container {
  /** Marker used to tell windows apart from a taskbar or desktop icons. */
  readonly isWindow: true;
  readonly modal: boolean;
  /** True while the window is parked. See `Window.minimize`. */
  readonly minimized: boolean;
  /** Notified when the window becomes (in)active. */
  setActive(active: boolean): void;
  /** Called when a click is rejected because a modal is open. */
  flashBlocked(): void;
  /** How tall this window's parked stub is under the current theme. */
  minimizedHeight(): number;
  /** The window this one is stacked above, minimized with and closed with. */
  readonly owner?: DesktopWindow | null;
}

/** What arranging windows asks of a `Window`, without core importing it. */
interface ArrangeableWindow {
  readonly maximized: boolean;
  readonly canResize: boolean;
  restore(): void;
}

/** True when a direct child of the desktop is a top-level window. */
export function isDesktopWindow(c: Control): c is DesktopWindow {
  return (c as unknown as Partial<DesktopWindow>).isWindow === true;
}

/**
 * Paint bands for the desktop's direct children. Windows are raised only
 * within their own band, so a taskbar in "foreground" always stays on top and
 * wallpaper or desktop icons in "background" always stay behind.
 */
/** Fraction of a fling's speed kept per 1/60s. */
const FLING_FRICTION = 0.94;
/** Below this, in content px per second, a fling is over. */
const FLING_MIN_SPEED = 40;

export type DesktopLayer = "background" | "window" | "minimized" | "foreground";

/** How hard the desktop works to keep windows on screen. See `windowClamp`. */
export type WindowClamp =
  /** Webwin never adjusts a window's bounds. */
  | "off"
  /** Enough of the title bar stays on screen to grab, as on Windows. */
  | "reachable"
  /** The whole window stays inside the work area. */
  | "viewport";

const LAYER_ORDER: Record<DesktopLayer, number> = {
  background: 0,
  window: 1,
  // Above the windows, below a taskbar: a parked stub the user cannot see is
  // no better than the window that vanished, and a maximized window would
  // cover the whole strip of them.
  minimized: 2,
  foreground: 3,
};

export interface DesktopOptions {
  theme: Theme;
  /** Continue rendering every frame instead of only when invalidated. */
  continuous?: boolean;
  /** Renderer options, for a desktop that makes its own game (`Desktop.create`). */
  gpu?: GpuContextOptions;
  /**
   * Draw over the game rather than in place of it: no desktop background, and a pointer or a key the
   * windows do not take falls through to the game. False for `Desktop.create`, which makes a game of its
   * own with nothing else in it; true by default for a desktop added to an existing game.
   */
  overlay?: boolean;
  /** Where among the game's layers the desktop draws. 1000 by default, above the game's scenes. */
  order?: number;
  /**
   * Multiplies the size of the whole UI. 1 is one logical pixel per CSS pixel.
   *
   * The UI-scale slider every game ships, and an accessibility control for
   * everything else. Distinct from the device pixel ratio, which the renderer
   * handles on its own and which does not change how much fits on screen.
   */
  uiScale?: number;
  /** Deadzone, repeat timing and button mapping for connected gamepads. */
  gamepad?: GamepadReaderOptions;
  /** Snap a window dragged to an edge of the work area, as Windows does. See `Desktop.snapWindows`. */
  snapWindows?: boolean;
  /** Keys for closing, cycling and the system menu, or false for none. See `Desktop.windowKeys`. */
  windowKeys?: WindowKeyMap | false;
  /**
   * Let an unhandled arrow key move focus to the nearest control that way.
   *
   * Off by default: arrows do not move focus in a desktop app, and a game's
   * menu is where they should. A gamepad's d-pad navigates either way.
   */
  arrowKeyNavigation?: boolean;
  /**
   * Grow a hidden DOM tree shadowing the controls, for a screen reader.
   *
   * Off by default, and an explicit verb rather than a default, for the same
   * reason `beginAutofill` is one: a game drawing a scene at sixty frames a
   * second has nothing to say to an assistive technology and should not pay a
   * tree walk to say it. Passing `true` takes the defaults.
   *
   * Read `skill/webwin/references/accessibility.md` before promising anybody
   * this makes an application accessible. It exposes structure, names, states
   * and text; it does nothing about contrast, focus visibility or reflow, and
   * it cannot help a crawler, which never gets a GPU and so never gets a tree.
   */
  accessibility?: boolean | AccessibilityOptions;
}

export interface AccessibilityOptions {
  /**
   * Key that opens the on-canvas navigation index. `null` turns it off.
   *
   * F6 by default, which is what a Windows application uses to move between
   * the panes of a window and is the nearest existing habit.
   */
  navigationIndexKey?: string | null;
  /** Milliseconds between tree walks. Focus changes ignore this. */
  snapshotIntervalMs?: number;
  /** `"all"` mirrors every row of a short list so Ctrl+F can find them. */
  itemPolicy?: "window" | "all";
  /** The most rows one control may mirror when `itemPolicy` is `"all"`. */
  itemCap?: number;
  /** What the whole surface is called. */
  label?: string;
}

/** Which way each arrow key points, for directional focus navigation. */
const ARROW_DIRECTIONS: Readonly<Record<string, FocusDirection | undefined>> = {
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
};

/**
 * The root of the windowing front-end: the window list and modal stack, focus, the UI's input and its
 * invalidate-driven painting - drawn as one layer of a `Game`.
 *
 * `Desktop.create(canvas, options)` makes a game with nothing else in it, for an application that is all
 * windows. `new Desktop(game, options)` lays the windowing UI over a game that is already running.
 */
export class Desktop extends Container implements GameLayer {
  /** Marker read by Control.desktop when walking to the root. */
  readonly asDesktop: Desktop = this;

  readonly game: Game;
  readonly gpu: GpuContext;
  readonly order: number;
  /** See `DesktopOptions.overlay`. */
  readonly overlay: boolean;
  readonly batcher: QuadBatcher;
  readonly rc: RenderContext;
  readonly images: ImageStore;
  readonly focusManager: FocusManager;
  readonly input: InputManager;
  readonly gamepads: GamepadReader;

  /** See `DesktopOptions.arrowKeyNavigation`. */
  arrowKeyNavigation: boolean;

  #uiScale = 1;
  readonly keyboard: KeyboardBridge;

  #theme: Theme;
  #dirty = true;
  #continuous: boolean;
  /** Whether this desktop made its game, and so ends it. */
  #ownsGame = false;
  #detach: (() => void)[] = [];
  #cursor = "default";
  #modalStack: DesktopWindow[] = [];
  #activeWindow: DesktopWindow | null = null;
  #layers = new WeakMap<Control, DesktopLayer>();
  #popups: Control[] = [];
  #workAreaInsets: Insets = { left: 0, top: 0, right: 0, bottom: 0 };
  #disposed = false;
  /** Controls that want a frame every tick (caret blink, animations). */
  #animators = new Set<Control>();
  /** `onFrame` steps, each with whether it asked for another frame last time it ran. */
  #frameSteps = new Map<(now: number) => boolean, boolean>();
  /** Live password-manager forms, each shadowing a few controls. */
  #autofill = new Set<AutofillForm>();
  /** The hidden DOM tree a screen reader reads, when one was asked for. */
  #aria: AriaMirror | null = null;
  #navigationIndex: NavigationIndex | null = null;
  /** Key that opens the navigation index. Null turns it off. */
  navigationIndexKey: string | null = null;
  /**
   * The frame clock. Seeded from the real clock rather than left at zero so a
   * timestamp taken before the first frame is measured against something real.
   */
  #frameTime = performance.now() / 1000;
  /** `#frameTime` as of the last paint, which is what the fling steps against. */
  #paintTime = this.#frameTime;
  /** True once the tick has stamped `#frameTime` for the frame now running. */
  #timeSampled = false;
  readonly tooltips: TooltipManager = new TooltipManager(this);

  constructor(game: Game, options: DesktopOptions) {
    const gpu = game.gpu;
    super({ x: 0, y: 0, width: gpu.size.width, height: gpu.size.height });
    this.game = game;
    this.gpu = gpu;
    this.overlay = options.overlay ?? true;
    this.order = options.order ?? 1000;
    this.snapWindows = options.snapWindows ?? !this.overlay;
    const keys = options.windowKeys ?? (this.overlay ? false : {});
    this.windowKeys = keys === false ? null : keys;
    this.#theme = options.theme;
    this.#continuous = options.continuous ?? false;
    this.batcher = new QuadBatcher(gpu);
    this.rc = new RenderContext(gpu, this.batcher, new GlyphAtlas(gpu));
    this.images = game.images;
    this.focusManager = new FocusManager(
      () => this.#activeWindow ?? this,
      // Text entry follows the focused control, so the hidden field is only
      // focused — and a phone's keyboard only raised — when one that takes
      // typing has focus.
      //
      // Order matters: the keyboard moves DOM focus first, and only then does
      // the mirror know which element to write `aria-activedescendant` onto.
      () => {
        this.keyboard?.sync();
        this.#aria?.onFocusChanged();
      },
    );
    this.keyboard = new KeyboardBridge(gpu.canvas, {
      keydown: (e) => this.#onKeyDown(e),
      keyup: (e) => this.#onKeyUp(e),
      textInput: (text) => this.#onTextInput(text),
      replaceText: (start, end, text) => this.#onReplaceText(start, end, text),
      compositionUpdate: (text) => this.#onCompositionUpdate(text),
      compositionEnd: () => this.#onCompositionUpdate(""),
      copyRequested: () => this.#onClipboardRead(false),
      cutRequested: () => this.#onClipboardRead(true),
      paste: (text) => this.#onTextInput(text, true),
      blur: () => this.invalidate(),
      textEntry: () => {
        const focused = this.focusManager.focused;
        return textEntryFor(focused, focused ? this.#autofillFieldFor(focused) : null);
      },
    });
    this.input = new InputManager(this, gpu.canvas);
    this.arrowKeyNavigation = options.arrowKeyNavigation ?? false;
    this.#uiScale = clampScale(options.uiScale ?? 1);
    this.gamepads = new GamepadReader(
      {
        key: (args) => {
          if (this.dispatchKeyDown(args)) this.invalidate();
        },
        button: (pad, index, pressed) => this.emit("gamepadbutton", { pad, index, pressed }),
        changed: () => this.emit("gamepadchanged"),
      },
      options.gamepad,
    );
    gpu.canvas.style.cursor = this.#cursor;
    this.#detach.push(
      game.onResize((size) => {
        this.setBounds(0, 0, size.width / (this.uiScale || 1), size.height / (this.uiScale || 1));
        this.clampWindows();
        this.invalidate();
      }),
      game.input.addClaimant(this.#claimant),
      game.addLayer(this),
    );
    if (options.accessibility) {
      this.beginAccessibility(options.accessibility === true ? {} : options.accessibility);
    }
  }

  /**
   * A desktop with a game of its own and nothing else in it: an application that is all windows. It paints
   * only when something changes, and ends its game when disposed.
   */
  static async create(canvas: HTMLCanvasElement, options: DesktopOptions): Promise<Desktop> {
    let desktop: Desktop | null = null;
    const game = await Game.create(canvas, {
      gpu: {
        ...options.gpu,
        onDeviceLost: (info) => {
          desktop?.rc.atlas.clear();
          options.gpu?.onDeviceLost?.(info);
        },
      },
      continuous: false,
    });
    desktop = new Desktop(game, { ...options, overlay: options.overlay ?? false });
    desktop.#ownsGame = true;
    return desktop;
  }

  /**
   * What the game's input router asks before it hands an event to the game. A desktop that is not an
   * overlay takes everything; an overlay takes what lands on a control, what continues a gesture one of
   * its controls began, and the keyboard while one of its controls has focus.
   */
  #claimant: InputClaimant = {
    claimsPointer: (x, y) => {
      if (!this.overlay) return true;
      if (this.input.capturedControl || this.input.panning || this.activeModal) return true;
      const scale = this.#uiScale || 1;
      const hit = this.hitTestScreen({ x: x / scale, y: y / scale });
      return hit !== null && hit !== this;
    },
    claimsKeys: () => {
      if (!this.overlay) return true;
      const focused = this.focusManager.focused;
      return (focused !== null && focused !== this) || this.activeModal !== null;
    },
  };

  // --- theme ------------------------------------------------------------

  get theme(): Theme {
    return this.#theme;
  }

  setTheme(theme: Theme): void {
    if (this.#theme === theme) return;
    this.#theme = theme;
    // Metrics and fonts changed: every control re-derives whatever it caches
    // from them, and anchors re-baseline so the next resize does not jump.
    this.notifyEnvironmentChanged();
    // Anything showing which theme is current — a picker, a settings page —
    // needs telling, or it goes stale the moment the theme is changed from
    // somewhere else.
    this.emit("themechanged");
    // Stubs are a caption tall, and a theme swap is exactly what changes that.
    this.invalidateMinimizedLayout();
    this.invalidate();
  }

  // --- windows and modality ---------------------------------------------

  /** Top-level windows, back to front. Excludes taskbars, icons and popups. */
  get windows(): DesktopWindow[] {
    return this.controls.toArray().filter(isDesktopWindow);
  }

  layerOf(control: Control): DesktopLayer {
    return this.#layers.get(control) ?? "window";
  }

  /**
   * Index just past the last child in `layer` — where a new child of that
   * layer belongs. `skip` is excluded from the count so a control can be
   * repositioned relative to its siblings.
   */
  #layerEnd(layer: DesktopLayer, skip?: Control): number {
    const order = LAYER_ORDER[layer];
    let count = 0;
    for (const c of this.controls) {
      if (c === skip) continue;
      if (LAYER_ORDER[this.layerOf(c)] <= order) count++;
    }
    return count;
  }

  #addToLayer<T extends Control>(control: T, layer: DesktopLayer): T {
    this.#layers.set(control, layer);
    this.controls.insert(this.#layerEnd(layer), control);
    return control;
  }

  /**
   * Raise a child to the front of its own layer, and no further. The windows it owns come up
   * with it, in the order they were in, so an owned window is never left behind its owner.
   */
  #raiseWithinLayer(control: Control): void {
    const layer = this.layerOf(control);
    this.controls.moveTo(control, this.#layerEnd(layer, control));
    if (!isDesktopWindow(control)) return;
    for (const owned of this.windows) {
      if (owned.owner === control && this.layerOf(owned) === layer) this.#raiseWithinLayer(owned);
    }
  }

  /** Lower a child to the back of its own layer, behind its siblings there and no further. */
  #lowerWithinLayer(control: Control): void {
    const order = LAYER_ORDER[this.layerOf(control)];
    let start = 0;
    for (const c of this.controls) if (c !== control && LAYER_ORDER[this.layerOf(c)] < order) start++;
    this.controls.moveTo(control, start);
  }

  /**
   * Activate the next open window, as Ctrl+F6 does: the one at the back comes forward, so
   * pressing it again walks through every window rather than flipping between two. `previous`
   * sends the active window to the back instead and activates the one that is then in front.
   */
  cycleWindows(direction: "next" | "previous" = "next"): void {
    if (this.activeModal) return;
    const open = this.windows.filter((w) => w.visible && !w.minimized && !w.owner);
    if (open.length < 2) return;
    if (direction === "next") {
      this.activate(open[0]!);
      return;
    }
    const active = this.#activeWindow;
    if (active) this.#lowerWithinLayer(active);
    const front = this.windows.filter((w) => w.visible && !w.minimized && !w.owner).pop();
    if (front) this.activate(front);
    this.invalidate();
  }

  /** Move an existing child into another band, at the front of it. */
  #moveToLayer(control: Control, layer: DesktopLayer): void {
    if (this.layerOf(control) === layer) return;
    this.#layers.set(control, layer);
    this.controls.moveTo(control, this.#layerEnd(layer, control));
  }

  /** Add a control behind every window — wallpaper, desktop icons. */
  addBackground<T extends Control>(control: T): T {
    return this.#addToLayer(control, "background");
  }

  /** Add a control in front of every window — a taskbar, a dock. */
  addForeground<T extends Control>(control: T): T {
    return this.#addToLayer(control, "foreground");
  }

  // --- work area ---------------------------------------------------------

  /**
   * Chrome to keep clear of. Maximized windows and centered dialogs use the
   * remaining work area rather than the whole desktop.
   */
  get workAreaInsets(): Insets {
    return this.#workAreaInsets;
  }
  set workAreaInsets(v: Insets) {
    this.#workAreaInsets = v;
    for (const w of this.windows) {
      const maximizable = w as Partial<{ maximized: boolean; applyMaximizedBounds(): void }>;
      if (maximizable.maximized) maximizable.applyMaximizedBounds?.();
    }
    // Maximized windows are re-bounded above, but anything else that lays
    // itself out against the work area — a HUD pinned to a screen edge — has
    // no way to know the area moved unless it is told.
    this.emit("workareachanged");
    this.invalidateMinimizedLayout();
    this.invalidate();
  }

  get workArea(): Rect {
    const i = this.#workAreaInsets;
    return {
      x: i.left,
      y: i.top,
      width: Math.max(0, this.width - i.left - i.right),
      height: Math.max(0, this.height - i.top - i.bottom),
    };
  }

  /**
   * How hard the desktop works to keep windows on screen.
   *
   * `viewport` is the default: a window is placed wholly inside the work
   * area, stays there when the canvas resizes, and cannot be dragged out of
   * it. `reachable` is the looser rule — enough of the title bar stays put to
   * grab, and the rest may hang over an edge, which is how Windows itself
   * behaves. `off` means Webwin never touches a window's bounds, for an
   * application that places them itself and means it.
   */
  windowClamp: WindowClamp = "viewport";

  /**
   * Let go of a window dragged to the left or right edge of the work area and it takes that
   * half; to a corner and it takes that quarter; to the top and it maximizes. Dragging it away
   * again gives it back the size it had. A preview shows where it will land while it is held.
   *
   * On by default for a desktop that is the whole application, off for one laid over a game,
   * where an edge of the screen is more often a place a HUD window was put on purpose.
   */
  snapWindows: boolean;

  /**
   * The window keys, laid over `DEFAULT_WINDOW_KEYS`: Ctrl+F4 closes the active window,
   * Ctrl+F6 and Ctrl+Shift+F6 cycle through the open ones, Alt+Hyphen opens its system menu,
   * whose Move and Size run from the arrow keys. Null turns them all off; a command mapped to
   * null or `[]` turns off that one. On by default except on a desktop laid over a game.
   */
  windowKeys: WindowKeyMap | null;

  /** How near an edge, in UI units, the pointer has to be to snap. */
  snapEdge = 8;

  #snapPreview: Rect | null = null;

  /** The rectangle a window drag would snap to, painted over everything. Set by `Window`. */
  get snapPreview(): Rect | null {
    return this.#snapPreview;
  }
  set snapPreview(r: Rect | null) {
    const was = this.#snapPreview;
    if (was === r || (was && r && was.x === r.x && was.y === r.y && was.width === r.width && was.height === r.height)) return;
    this.#snapPreview = r;
    this.invalidate();
  }

  /**
   * Overlap the open windows down and to the right from the work area's top-left, back to
   * front, as Win32's Cascade does. Maximized windows are restored first; minimized, hidden,
   * modal and owned ones - a palette belongs beside its owner, not in the cascade - are left
   * where they are.
   */
  cascadeWindows(): void {
    const wins = this.#arrangeable();
    const step = this.#theme.metrics.titleBarHeight + this.#theme.metrics.windowBorder;
    const rects = cascadeRects(wins.map((w) => w.size), this.workArea, Math.max(16, step));
    wins.forEach((w, i) => this.#place(w, rects[i]!));
    if (wins.length > 0) this.activate(wins[wins.length - 1]!);
  }

  /** Tile the open windows over the work area: `vertical` side by side, `horizontal` stacked. */
  tileWindows(orientation: TileOrientation = "vertical"): void {
    const wins = this.#arrangeable();
    const rects = tileRects(wins.length, this.workArea, orientation);
    wins.forEach((w, i) => this.#place(w, rects[i]!));
  }

  #arrangeable(): DesktopWindow[] {
    const wins = this.windows.filter((w) => w.visible && !w.minimized && !w.modal && !w.owner);
    for (const w of wins) {
      const win = w as Partial<ArrangeableWindow>;
      if (win.maximized) win.restore?.();
    }
    return wins;
  }

  #place(win: DesktopWindow, r: Rect): void {
    // A fixed-size window keeps its size and only moves to its slot.
    if ((win as Partial<ArrangeableWindow>).canResize === false) win.setLocation(r.x, r.y);
    else win.setBounds(r.x, r.y, r.width, r.height);
  }

  /** Below this work-area width, `compact` is true. */
  compactWidth = 640;

  /**
   * True when there is not enough room for a windowed layout — a phone, or a
   * narrow browser window.
   *
   * The framework does not act on this itself, because what to do about it is
   * an application's decision: one app might maximize everything, a gallery of
   * fourteen windows would rather cascade them. It is here so that decision
   * has one thing to ask.
   */
  get compact(): boolean {
    return this.workArea.width < this.compactWidth;
  }

  /**
   * Re-apply `windowClamp` to every window.
   *
   * Called on canvas resize, so rotating a phone or dragging a browser window
   * narrower does not strand a window past the edge with no way to grab it.
   */
  clampWindows(): void {
    if (this.windowClamp === "off") return;
    for (const w of this.windows) {
      (w as Partial<{ applyClamp(): void }>).applyClamp?.();
    }
  }

  // --- minimized windows --------------------------------------------------

  /**
   * How wide a parked stub is.
   *
   * Stubs are all one width, like the icons a minimized window used to
   * become, so the strip stays a strip instead of a ragged row of titles.
   */
  minimizedWidth = 168;

  /** Space between parked stubs, and between the strip and the work area. */
  minimizedGap = 6;

  /**
   * Where a window goes when it is minimized, if something has undertaken to
   * show it there.
   *
   * A taskbar sets this and returns the rectangle of the button that stands
   * for `win`, in desktop coordinates. Two things follow from a claim: the
   * desktop stops parking that window — it draws no stub for it, because the
   * taskbar is already showing it — and the minimize animation shrinks
   * towards the button instead of towards the corner, so the window visibly
   * goes to the place the user has to click to get it back.
   *
   * Return null, or leave the hook unset, and the window parks: the desktop
   * tiles a stub for it along the bottom of the work area. That is the point
   * of the default — an application that never builds a taskbar still cannot
   * lose a window down a hole.
   *
   * ```ts
   * desktop.minimizeTarget = (win) => taskbar.buttonFor(win)?.screenBounds ?? null;
   * ```
   *
   * Called while laying the strip out and again when an animation starts, so
   * it should be cheap and it must not minimize or restore anything itself.
   */
  minimizeTarget: ((win: DesktopWindow) => Rect | null) | null = null;

  /**
   * How long a window takes to shrink into its minimized place, and to grow
   * back out of it, in milliseconds. Zero turns the animation off.
   *
   * The animation is what makes minimizing legible: a window that fades out
   * where it stood tells the user nothing about where to find it, and one
   * that visibly flies to a taskbar button or to a stub in the corner tells
   * them exactly that. Defaults to off when the browser says the user
   * prefers reduced motion, which is the one case where the movement costs
   * more than the explanation is worth.
   */
  minimizeAnimationMs = prefersReducedMotion() ? 0 : 140;

  /**
   * Whether motion should be kept to a minimum: entrances land at once, loops stand still,
   * hover and press reactions jump rather than glide.
   *
   * Starts as whatever the browser says the user asked their system for, and is a field rather
   * than a live query so an application can offer its own setting, or a test can pin it.
   */
  reducedMotion = prefersReducedMotion();

  /** Minimized windows in the order they were minimized: their slot order. */
  #minimizedOrder: DesktopWindow[] = [];
  /** Where each parked window is drawn. Rebuilt whenever the strip moves. */
  #parked = new Map<DesktopWindow, Rect>();
  #parkedDirty = true;

  /** Minimized windows, in the order they were minimized. */
  get minimizedWindows(): readonly DesktopWindow[] {
    return this.#minimizedOrder;
  }

  /**
   * Told by a window that it has been minimized or restored.
   *
   * Part of the flow between `Window` and the desktop rather than something
   * an application calls: `win.minimize()` and `win.restore()` are the API.
   */
  notifyMinimized(win: DesktopWindow, minimized: boolean): void {
    const at = this.#minimizedOrder.indexOf(win);
    if (minimized) {
      if (at < 0) this.#minimizedOrder.push(win);
    } else if (at >= 0) {
      this.#minimizedOrder.splice(at, 1);
      this.#parked.delete(win);
    }
    // A parked stub belongs above the windows it was one of, or the first
    // maximized window would bury the whole strip.
    this.#moveToLayer(win, minimized ? "minimized" : "window");
    this.invalidateMinimizedLayout();
    this.emit("windowschanged");
  }

  /** Re-tile the strip of parked stubs before it is next asked about. */
  invalidateMinimizedLayout(): void {
    this.#parkedDirty = true;
    this.invalidate();
  }

  /**
   * Where `win` sits while it is parked, in desktop coordinates, or null if
   * it is not parked at all.
   *
   * Null covers two cases that look the same from here: the window is not
   * minimized, or it is minimized and something else — a taskbar, via
   * `minimizeTarget` — has undertaken to show it, in which case the desktop
   * draws nothing for it.
   */
  parkedRect(win: DesktopWindow): Rect | null {
    if (this.#parkedDirty) this.#layoutMinimized();
    return this.#parked.get(win) ?? null;
  }

  /**
   * Where minimizing `win` should send it: the button a taskbar claimed it
   * with, or the stub the desktop parked it in.
   *
   * The destination of the minimize animation, and the place a restore
   * animation comes back from. Null when there is nowhere to go — the window
   * is not minimized, or it is hidden and has no stub.
   */
  minimizedRect(win: DesktopWindow): Rect | null {
    if (!win.minimized) return null;
    return this.minimizeTarget?.(win) ?? this.parkedRect(win);
  }

  /**
   * Tile the parked windows along the bottom of the work area, left to
   * right, wrapping upwards when a row runs out of room.
   *
   * Bottom-left because that is the corner a desktop's own chrome is least
   * likely to want and the one a user's eye already treats as the parking
   * space — and because a window dragged over the strip covers it, which is
   * fine: the strip is where things are found, not where they live.
   */
  #layoutMinimized(): void {
    this.#parkedDirty = false;
    this.#parked.clear();
    const order = this.#minimizedOrder;
    // A window the application hid before minimizing it has nothing to show,
    // and a stub for it would be a button that lies about what is there. A
    // height of zero keeps its place in the order without taking a slot, and
    // it gets one back when it is shown again.
    const claim = this.minimizeTarget;
    const heights = order.map((win) =>
      // A claimed window is already being shown by whatever claimed it, and
      // a stub as well would be showing it twice.
      win.visible && !(claim && claim(win)) ? win.minimizedHeight() : 0,
    );
    const slots = tileMinimizedStrip(heights, this.workArea, {
      width: this.minimizedWidth,
      gap: this.minimizedGap,
    });
    for (let i = 0; i < order.length; i++) {
      const slot = slots[i];
      if (slot) this.#parked.set(order[i]!, slot);
    }
  }

  // --- popups ------------------------------------------------------------

  /** The topmost open popup, or null. */
  get popup(): Control | null {
    return this.#popups[this.#popups.length - 1] ?? null;
  }

  /** The whole popup stack, outermost first. A menu chain is one entry each. */
  get popups(): readonly Control[] {
    return this.#popups;
  }

  /**
   * Show a transient control — a menu, a drop-down — above everything.
   *
   * Popups nest: showing one while another is open pushes it on top, which is
   * what a cascading submenu needs. Pass `nested: false` (the default) to
   * replace the stack instead, which is what an unrelated drop-down wants.
   * Escape closes the topmost; a press outside closes back to whichever popup
   * was clicked in.
   */
  showPopup<T extends Control>(control: T, options: { nested?: boolean } = {}): T {
    if (!options.nested) this.closeAllPopups();
    else if (this.#popups.includes(control)) this.closePopup(control);
    this.#popups.push(control);
    if (control.parent !== this) this.#addToLayer(control, "foreground");
    this.#raiseWithinLayer(control);
    control.visible = true;
    this.invalidate();
    return control;
  }

  /**
   * Close `control` and every popup above it. With no argument, closes just
   * the topmost — one level of a menu chain.
   */
  closePopup(control?: Control): void {
    const index = control ? this.#popups.indexOf(control) : this.#popups.length - 1;
    if (index < 0) return;
    const doomed = this.#popups.splice(index);
    // Topmost first, so a parent menu is never briefly the target of input
    // meant for the child that is still being torn down.
    for (const popup of doomed.reverse()) {
      this.input.forget(popup);
      this.focusManager.clearWithin(popup);
      this.controls.remove(popup);
      // Whoever opened this needs to know, or its idea of what is open goes
      // stale the moment a press elsewhere dismisses the stack.
      popup.emit("closed");
    }
    this.invalidate();
  }

  closeAllPopups(): void {
    if (this.#popups.length > 0) this.closePopup(this.#popups[0]);
  }

  /**
   * Called by the input manager before routing a press. Returns true when the
   * press was consumed dismissing popups, the way Windows menus swallow the
   * click that closes them.
   *
   * A press inside an open popup closes anything stacked above it and then
   * proceeds, so clicking a different item in a parent menu works. A press
   * outside them all closes the lot and is swallowed.
   */
  dismissPopupIfOutside(target: Control | null): boolean {
    if (this.#popups.length === 0) return false;
    let deepest = -1;
    for (let i = this.#popups.length - 1; i >= 0; i--) {
      if (target && this.#popups[i]!.contains(target)) {
        deepest = i;
        break;
      }
    }
    if (deepest === this.#popups.length - 1) return false; // inside the topmost
    if (deepest >= 0) {
      this.closePopup(this.#popups[deepest + 1]);
      return false;
    }
    this.closeAllPopups();
    return true;
  }

  get activeWindow(): DesktopWindow | null {
    return this.#activeWindow;
  }

  /** The modal currently capturing input, or null. */
  get activeModal(): DesktopWindow | null {
    return this.#modalStack[this.#modalStack.length - 1] ?? null;
  }

  addWindow<T extends DesktopWindow>(win: T): T {
    this.#addToLayer(win, "window");
    // Clamp before activating: a layout designed for a laptop puts windows
    // wherever it likes, and on a phone that is usually off the edge.
    if (this.windowClamp !== "off") {
      (win as Partial<{ fitIntoWorkArea(): void }>).fitIntoWorkArea?.();
    }
    this.activate(win);
    this.emit("windowschanged");
    return win;
  }

  removeWindow(win: DesktopWindow): void {
    const parked = this.#minimizedOrder.indexOf(win);
    if (parked >= 0) {
      this.#minimizedOrder.splice(parked, 1);
      this.#parked.delete(win);
      this.invalidateMinimizedLayout();
    }
    this.popModal(win);
    this.removeAnimator(win);
    this.input.forget(win);
    this.focusManager.clearWithin(win);
    this.controls.remove(win);
    if (this.#activeWindow === win) {
      this.#activeWindow = null;
      this.emit("activewindowchanged");
      const next = this.#topmostActivatable();
      if (next) this.activate(next);
    }
    this.emit("windowschanged");
    this.invalidate();
  }

  pushModal(win: DesktopWindow): void {
    if (!this.#modalStack.includes(win)) this.#modalStack.push(win);
    this.activate(win);
  }

  popModal(win: DesktopWindow): void {
    const i = this.#modalStack.indexOf(win);
    if (i >= 0) this.#modalStack.splice(i, 1);
    const next = this.activeModal ?? this.#topmostActivatable();
    if (next) this.activate(next);
    this.invalidate();
  }

  #topmostActivatable(): DesktopWindow | null {
    const items = this.controls;
    for (let i = items.length - 1; i >= 0; i--) {
      const c = items.at(i);
      if (c && isDesktopWindow(c) && c.visible && !c.minimized) return c;
    }
    return null;
  }

  activate(win: DesktopWindow): void {
    if (this.#activeWindow === win) {
      this.#raiseWithinLayer(win);
      return;
    }
    const previous = this.#activeWindow;
    this.#activeWindow = win;
    previous?.setActive(false);
    this.#raiseWithinLayer(win);
    win.setActive(true);
    // A parked window has no caret to show and nothing to click: focus stays
    // where it was until the window is restored, which puts it back itself.
    if (!win.minimized) this.focusManager.restoreIn(win);
    this.emit("activewindowchanged");
    this.invalidate();
  }

  /**
   * Activate the top-level window owning `control`. Clicking a taskbar or a
   * desktop icon must not change which window is active, so non-window
   * top-level children are ignored.
   */
  activateFor(control: Control): void {
    let c: Control | null = control;
    while (c && c.parent && c.parent !== this) c = c.parent;
    if (c && c.parent === this && isDesktopWindow(c)) this.activate(c);
  }

  /** False when a modal is open and `control` is outside it. */
  allowsInput(control: Control): boolean {
    const modal = this.activeModal;
    if (!modal) return true;
    return modal.contains(control);
  }

  onBlockedByModal(_control: Control): void {
    this.activeModal?.flashBlocked();
  }

  onBackgroundPointerDown(p: Point, e: MouseSource): void {
    if (this.activeModal) {
      this.activeModal.flashBlocked();
      return;
    }
    this.focusManager.clear();
    const args = makeMouseArgs(p.x, p.y, p, e);
    // Let applications react to a press on bare desktop (deselecting icons).
    this.emit("pointerdown", args);
    // Bare desktop has no control to track a press against, so the context
    // menu fires on the press rather than the release as it does elsewhere.
    // There is no drag to disambiguate here, so the difference does not show.
    if (e.button === 2) this.emit("contextmenu", args);
  }

  // --- hit testing -------------------------------------------------------

  /** Deepest control at a canvas point, or null. */
  hitTestScreen(p: Point): Control | null {
    return this.hitTest(p);
  }

  protected override hitTestSelf(): boolean {
    // The desktop background is not a click target itself.
    return false;
  }

  /**
   * The strip of parked stubs is measured from the work area, so every route
   * that changes the desktop's size — a resized canvas, a rotated phone, the
   * UI scale — has to re-tile it. They all end up here.
   */
  protected override onResize(): void {
    super.onResize();
    this.invalidateMinimizedLayout();
  }

  // --- ui scale -----------------------------------------------------------

  /**
   * How much bigger than a CSS pixel a Webwin pixel is.
   *
   * Everything above the canvas — layout, hit testing, control coordinates —
   * works in these units, and the two places that know otherwise are
   * `render`, which scales the paint, and the input layer, which divides
   * incoming pointer positions back down. So raising it makes every control
   * larger and fits fewer of them on screen, which is what a UI-scale slider
   * is for; it is not the device pixel ratio, which is about sharpness and is
   * handled inside the renderer.
   */
  get uiScale(): number {
    return this.#uiScale;
  }

  set uiScale(value: number) {
    const next = clampScale(value);
    if (next === this.#uiScale) return;
    this.#uiScale = next;
    this.#syncBounds();
    this.clampWindows();
    this.invalidate();
  }

  /** Resize the desktop to the canvas, in UI units. */
  #syncBounds(): void {
    const width = this.gpu.size.width / this.#uiScale;
    const height = this.gpu.size.height / this.#uiScale;
    if (this.width !== width || this.height !== height) this.setBounds(0, 0, width, height);
  }

  // --- drag and drop ------------------------------------------------------

  #drag: DragSession | null = null;

  /** The drag in flight, or null. */
  get drag(): DragSession | null {
    return this.#drag;
  }

  /**
   * Pick `payload` up and follow the pointer with it.
   *
   * Called by a control from its own `pointermove`, once the pointer has
   * traveled far enough to mean a drag rather than a click. From here the
   * session is modal: moves and the release go to it, and the press that
   * started it never becomes a click.
   */
  beginDrag(payload: DragPayload, options: DragOptions = {}): DragSession {
    this.#drag?.cancel();
    this.tooltips.hide();
    const session = new DragSession(this, payload, options);
    this.#drag = session;
    // The ghost is deliberately *not* added to the control tree. It is painted
    // as an overlay instead, which is the only way it cannot end up hit-tested,
    // focused or docked — and a ghost that answers a hit test is standing
    // between the pointer and the thing it is being dropped on.
    if (options.ghost) session.moveTo(this.input.pointerPosition);
    this.invalidate();
    return session;
  }

  /** Called by the session when it ends. Not part of the public flow. */
  endDrag(session: DragSession): void {
    if (this.#drag === session) this.#drag = null;
  }

  // --- cursor ------------------------------------------------------------

  setCursor(cursor: string): void {
    if (this.#cursor === cursor) return;
    this.#cursor = cursor;
    this.gpu.canvas.style.cursor = cursor;
  }

  // --- keyboard ----------------------------------------------------------

  #onKeyDown(e: KeyboardEvent): void {
    const args = makeKeyArgs(e);
    if (this.dispatchKeyDown(args)) {
      e.preventDefault();
      return;
    }
    // Swallow keys that would otherwise scroll or navigate the host page.
    const editing = this.focusManager.focused?.acceptsTextInput ?? false;
    if (shouldSuppressKey(args, editing)) e.preventDefault();
  }

  /**
   * Route a key press: focused control and its ancestors first, then the
   * global handlers. Returns whether anything took it.
   *
   * Public because a gamepad button arrives this way — it is turned into the
   * key it stands for and dispatched here, so no control needs to know a pad
   * exists.
   */
  dispatchKeyDown(args: KeyEventArgs): boolean {
    if (this.tooltips.hideOnKey) this.tooltips.hide();

    // A drag outranks everything: Escape drops it, and no control gets a look
    // in while one is running, or a list would move its selection under it.
    if (this.#drag) {
      if (args.key === "Escape") this.#drag.cancel();
      return true;
    }
    // So does a keyboard Move or Size from the system menu: the arrows are the window's.
    const tracking = this.#activeWindow as Partial<{ handleTrackingKey(a: KeyEventArgs): boolean }> | null;
    if (tracking?.handleTrackingKey?.(args)) return true;

    // Give the focused control and its ancestors first refusal.
    let control: Control | null = this.focusManager.focused;
    while (control) {
      if (control.effectivelyEnabled) {
        control.emit("keydown", args);
        if (args.handled) return true;
      }
      control = control.parent;
    }
    return this.#handleGlobalKey(args);
  }

  #onKeyUp(e: KeyboardEvent): void {
    const args = makeKeyArgs(e);
    let control: Control | null = this.focusManager.focused;
    while (control) {
      if (control.effectivelyEnabled) {
        control.emit("keyup", args);
        if (args.handled) return;
      }
      control = control.parent;
    }
  }

  #onTextInput(text: string, paste = false): void {
    const control = this.focusManager.focused;
    if (!control || !control.effectivelyEnabled) return;
    const args: TextInputArgs = { text, handled: false, paste };
    control.emit("textinput", args);
  }

  /**
   * An edit the hidden field made to the focused control's mirrored text.
   * A control that keeps no text of its own gets what was inserted, as typing.
   */
  #onReplaceText(start: number, end: number, text: string): void {
    const control = this.focusManager.focused as
      | (Control & { replaceTextRange?: (start: number, end: number, text: string) => void })
      | null;
    if (!control || !control.effectivelyEnabled) return;
    if (control.replaceTextRange) {
      control.replaceTextRange(start, end, text);
      this.invalidate();
    } else if (text) {
      this.#onTextInput(text);
    }
  }

  #onCompositionUpdate(text: string): void {
    const control = this.focusManager.focused as (Control & { setComposition?: (s: string) => void }) | null;
    control?.setComposition?.(text);
    this.invalidate();
  }

  #onClipboardRead(cut: boolean): string | null {
    const control = this.focusManager.focused as
      | (Control & { getClipboardText?: (cut: boolean) => string | null })
      | null;
    return control?.getClipboardText?.(cut) ?? null;
  }

  /** Tab traversal and dialog Enter/Escape, after controls decline the key. */
  #handleGlobalKey(args: KeyEventArgs): boolean {
    if (args.key === "Escape" && this.#popups.length > 0) {
      this.closePopup();
      return true;
    }
    if (this.windowKeys && this.#runWindowCommand(args)) return true;
    if (
      this.navigationIndexKey !== null &&
      args.key === this.navigationIndexKey &&
      !args.ctrlKey &&
      !args.altKey &&
      !args.metaKey
    ) {
      this.showNavigationIndex();
      return true;
    }
    if (args.key === "Tab" && !args.ctrlKey && !args.altKey && !args.metaKey) {
      if (args.shiftKey) this.focusManager.movePrevious();
      else this.focusManager.moveNext();
      return true;
    }
    const win = this.activeModal ?? this.#activeWindow;
    const handler = win as (Container & { handleDialogKey?: (a: KeyEventArgs) => boolean }) | null;
    if (handler?.handleDialogKey?.(args) ?? false) return true;

    // An arrow nobody wanted moves focus that way. Off for the keyboard by
    // default — arrows do not move focus in a desktop app, and turning that
    // on under every existing app would be a surprise — but always on for a
    // pad, where a d-pad has nothing else to mean.
    const direction = ARROW_DIRECTIONS[args.key];
    if (direction && (this.arrowKeyNavigation || isGamepadCode(args.code))) {
      return this.focusManager.navigate(direction);
    }
    return false;
  }

  #runWindowCommand(args: KeyEventArgs): boolean {
    const command = windowCommandFor(args, this.windowKeys ?? {});
    if (!command) return false;
    const win = (this.activeModal ?? this.#activeWindow) as
      | (DesktopWindow & Partial<{ canClose: boolean; close(): boolean; showSystemMenu(): void }>)
      | null;
    switch (command) {
      case "close":
        if (win?.canClose !== false) win?.close?.();
        break;
      case "next":
      case "previous":
        this.cycleWindows(command);
        break;
      case "systemMenu":
        win?.showSystemMenu?.();
        break;
    }
    return true;
  }

  // --- rendering ---------------------------------------------------------

  /** Ask for a repaint on the next animation frame. */
  override invalidate(): void {
    this.#dirty = true;
  }

  /**
   * The frame clock, in seconds, stamped once per animation frame.
   *
   * Read from a single stamp rather than calling the clock per control, so
   * everything animating in one frame agrees on when "now" is. The stamp is
   * taken every frame, painted or not, so a value stamped from an event
   * handler on an idle desktop still ages correctly in a later paint. Only
   * differences are meaningful: the origin is the page's, not the desktop's.
   */
  get frameTime(): number {
    return this.#frameTime;
  }

  /** Hide any visible tooltip; called when input makes it stale. */
  hideTooltip(): void {
    this.tooltips.hide();
  }

  /** Repaint every frame while `control` is registered (caret blink, drags). */
  // --- flick scrolling ----------------------------------------------------

  #fling: { target: Control; vx: number; vy: number } | null = null;

  /**
   * Carry on scrolling after the finger leaves, slowing to a stop.
   *
   * `vx`/`vy` are the throw, in content pixels per second, in the same sense
   * as `panBy`. Stepped from `render`, because a fling is a property of the
   * desktop's clock rather than of any one control — and it registers as an
   * animator so the frame loop keeps running while it decays.
   */
  startFling(target: Control, vx: number, vy: number): void {
    this.stopFling();
    if (Math.hypot(vx, vy) < FLING_MIN_SPEED) return;
    this.#fling = { target, vx, vy };
    this.addAnimator(target);
  }

  /** Cut a fling short — any new touch does this. */
  stopFling(): void {
    const fling = this.#fling;
    this.#fling = null;
    if (fling) this.removeAnimator(fling.target);
  }

  get flinging(): boolean {
    return this.#fling !== null;
  }

  #stepFling(dt: number): void {
    const fling = this.#fling;
    if (!fling || dt <= 0) return;
    // Framerate-independent decay: the same fraction is shed per second
    // however often frames happen to arrive.
    const decay = Math.pow(FLING_FRICTION, dt * 60);
    const wantX = fling.vx * dt;
    const wantY = fling.vy * dt;
    let dx = wantX;
    let dy = wantY;

    for (let c: Control | null = fling.target; c; c = c.parent) {
      if (!c.effectivelyEnabled) continue;
      const used = c.panBy(dx, dy);
      dx -= used.x;
      dy -= used.y;
      if (dx === 0 && dy === 0) break;
    }
    // Nothing at all was taken: the content has run out, so stop rather than
    // coast on against an end that will never move.
    if (dx === wantX && dy === wantY) {
      this.stopFling();
      return;
    }
    fling.vx *= decay;
    fling.vy *= decay;
    if (Math.hypot(fling.vx, fling.vy) < FLING_MIN_SPEED) this.stopFling();
    this.invalidate();
  }

  /**
   * Run `step` before every frame is painted, with the frame's time in seconds. While it returns
   * true the desktop keeps painting; once it returns false the desktop may go idle, and the step
   * is next run on whatever frame something else asks for. Returns the function that removes it.
   *
   * For state that is not a control but moves with the clock - a value easing toward a target, a
   * list of tips taking turns - so it is advanced once per frame, before anything reads it.
   */
  onFrame(step: (now: number) => boolean): () => void {
    this.#frameSteps.set(step, true);
    this.invalidate();
    return () => {
      this.#frameSteps.delete(step);
    };
  }

  addAnimator(control: Control): void {
    this.#animators.add(control);
    this.invalidate();
  }

  removeAnimator(control: Control): void {
    this.#animators.delete(control);
  }

  /**
   * True when some animator can actually be seen.
   *
   * Most animators stop themselves from inside their own paint, and a control
   * that is hidden, sits on a hidden tab or has been removed is never painted
   * again - so a spinner on a closed tab kept the whole desktop repainting at
   * sixty a second. They stay registered rather than being pruned: each keeps
   * its own flag for whether it registered, and dropping it here would leave
   * that flag saying yes and the animation frozen once it is shown again.
   * Showing it invalidates, and the frame that paints it picks it back up.
   */
  #wantsFrame(): boolean {
    for (const wants of this.#frameSteps.values()) if (wants) return true;
    for (const c of this.#animators) {
      if (c.effectivelyVisible && c.desktop === this) return true;
    }
    return false;
  }

  /**
   * Offer a set of fields to the browser's password manager.
   *
   * The one thing on a canvas that cannot be drawn: a saved credential lives
   * behind a browser dialog, and the browser only ever hands one to a real
   * `<input>`. So the form named here gets one per control, invisible and
   * exactly over it, and the values are mirrored both ways - see
   * `core/autofill.ts` for what that costs.
   *
   * Dispose the result when the form goes away, and only *after* a sign-in has
   * succeeded: a form removed on success is how a page that never navigates
   * tells a manager there was something worth saving.
   */
  beginAutofill(fields: readonly AutofillFieldSpec[]): AutofillForm {
    const parent = this.gpu.canvas.parentElement ?? document.body;
    const desktop = this;
    const form = new AutofillForm(
      {
        get uiScale(): number {
          return desktop.uiScale;
        },
        get composing(): boolean {
          return desktop.keyboard.composing;
        },
        invalidate: () => this.invalidate(),
        adoptKeyboardHost: (el) => this.keyboard.adopt(el as TextEntryField),
        releaseKeyboardHost: (el) => {
          this.keyboard.release(el as TextEntryField);
          // The field just released may be the one holding DOM focus, and
          // keystrokes have to land somewhere.
          this.keyboard.sync();
        },
      },
      parent,
      fields,
    );
    this.#autofill.add(form);
    // A manager can fill the moment the form exists, so the focused control
    // has to start using its field rather than the shared hidden one.
    this.keyboard.sync();
    return form;
  }

  /**
   * Grow the hidden DOM tree a screen reader reads.
   *
   * Called for you when `DesktopOptions.accessibility` is set; public so an
   * application can turn it on later, from a settings screen, without
   * rebuilding the desktop.
   *
   * The mirror is the only thing in Webwin that makes the interface visible
   * to anything but a pair of eyes, and it is still not the same as being
   * accessible - see `AccessibilityOptions`.
   */
  beginAccessibility(options: AccessibilityOptions = {}): AriaMirror {
    if (this.#aria && !this.#aria.disposed) return this.#aria;
    registerAccessibleDescribers();
    const desktop = this;
    const parent = this.gpu.canvas.parentElement ?? document.body;
    this.#aria = new AriaMirror(
      {
        get uiScale(): number {
          return desktop.uiScale;
        },
        get root(): Desktop {
          return desktop;
        },
        get modal(): Control | null {
          return desktop.activeModal as Control | null;
        },
        get focused(): Control | null {
          return desktop.focusManager.focused;
        },
        get keyboardHost(): HTMLElement {
          return desktop.keyboard.keyboardHost;
        },
        get canvas(): HTMLElement {
          return desktop.gpu.canvas;
        },
        get itemPolicy(): "window" | "all" {
          return options.itemPolicy ?? "window";
        },
        get itemCap(): number {
          return options.itemCap ?? DEFAULT_ITEM_CAP;
        },
        activate: (control, itemIndex) => this.activateAccessible(control, itemIndex),
      },
      parent,
      { snapshotIntervalMs: options.snapshotIntervalMs, label: options.label },
    );
    // The field the caret is in stops being hidden from readers, which is
    // where most of the value of this whole layer actually comes from.
    this.keyboard.setAccessible(true);
    this.navigationIndexKey = options.navigationIndexKey === undefined ? "F6" : options.navigationIndexKey;
    return this.#aria;
  }

  /** Stop mirroring, and leave the page as it was found. */
  endAccessibility(): void {
    this.#aria?.dispose();
    this.#aria = null;
    this.keyboard.setAccessible(false);
  }

  /**
   * Open the on-canvas outline of the desktop.
   *
   * The keyboard counterpart to the mirror, for somebody who can see the
   * screen and cannot reach the third window back without a lot of Tab. Also
   * useful behind an application's own button, which is why it is a verb and
   * not only a key.
   */
  showNavigationIndex(): NavigationIndex {
    const index = (this.#navigationIndex ??= new NavigationIndex());
    // Refreshed before it is placed, because refreshing sizes it and the
    // place it goes depends on how tall it turned out.
    if (index.parent !== this) this.#addToLayer(index, "foreground");
    index.refresh();
    const at = fitInWorkArea(
      this,
      { x: this.workArea.x + 24, y: this.workArea.y + 24 },
      { width: index.width, height: index.height },
    );
    index.setBounds(at.x, at.y, index.width, index.height);
    this.showPopup(index);
    this.focusManager.set(index);
    return index;
  }

  /** The live mirror, or null when accessibility was never turned on. */
  get accessibility(): AriaMirror | null {
    return this.#aria;
  }

  /**
   * Press a control on behalf of a screen reader.
   *
   * A reader activates by asking the browser to perform an accessible
   * object's default action, which arrives as a click on the mirror element.
   * Focus moves first, because that is what a pointer press would also do and
   * what several controls read to decide what a click means.
   *
   * The press is synthesized at the control's own center with `makeMouseArgs`,
   * which exists for exactly this - a press nobody's hand made.
   */
  activateAccessible(control: Control, itemIndex: number | null): void {
    if (control.inputBlocked || !control.effectivelyEnabled) return;
    if (control.focusable) this.focusManager.set(control);
    void itemIndex;
    const local = { x: control.width / 2, y: control.height / 2 };
    const bounds = control.screenBounds;
    const args = makeMouseArgs(
      local.x,
      local.y,
      { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 },
      { button: 0 },
    );
    control.emit("pointerdown", args);
    control.emit("pointerup", args);
    control.emit("click", args);
    this.invalidate();
  }

  #autofillFieldFor(control: Control): TextEntryField | null {
    for (const form of this.#autofill) {
      if (form.disposed) {
        this.#autofill.delete(form);
        continue;
      }
      const field = form.fieldFor(control);
      if (field) return field;
    }
    return null;
  }

  /** Start the game's loop, which paints the desktop. */
  start(): void {
    if (!this.#disposed) this.game.start();
  }

  stop(): void {
    this.game.stop();
  }

  // --- GameLayer ------------------------------------------------------------

  beginFrame(now: number): void {
    if (this.#disposed) return;
    // Stamped before the dirty check, not inside `render`, so the clock
    // keeps running while the desktop is idle. Stamped inside `render` it
    // froze at the last paint, and anything that took a timestamp from an
    // input handler — a spawned float, a toast, an expander — was born
    // against a clock that then jumped forward by the whole idle period and
    // was reaped as ancient on the frame that should have drawn it first.
    this.#frameTime = now / 1000;
    this.#timeSampled = true;
    // Polled before the dirty check: a pad press is what makes the frame
    // dirty, so testing first would sit on every input for a frame.
    this.gamepads.poll(now);
  }

  needsFrame(): boolean {
    return this.#dirty || this.#continuous || this.#wantsFrame();
  }

  /**
   * Paint one frame and read it back — a screenshot of exactly what the GPU drew.
   *
   * A browser screenshot of the canvas also works and agrees with this to within rounding; what this
   * adds is a frame you CHOSE - the game's whole frame, drawn again into a target and read back - and a
   * capture path for an embedder that has no page to screenshot. See `engine/capture.ts`.
   *
   * ```ts
   * const png = await desktop.capture({ maxWidth: 1600 });   // a Blob
   * const url = await desktop.captureDataUrl();              // for page.evaluate
   * ```
   */
  capture(options?: CaptureOptions): Promise<Blob> {
    return this.game.capture(options);
  }

  /** The same capture as a `data:image/png;base64,…` URL — the form that survives `page.evaluate`. */
  captureDataUrl(options?: CaptureOptions): Promise<string> {
    return this.game.captureDataUrl(options);
  }

  /** The same capture as raw RGBA8 pixels, for a golden-image comparison that wants no encoder. */
  capturePixels(options?: CaptureOptions): Promise<CapturedFrame> {
    return this.game.capturePixels(options);
  }

  /** Paint one frame immediately - the whole game's frame, of which the desktop is a layer. */
  render(): void {
    if (!this.#disposed) this.game.render();
  }

  /** The paint walk: every control records its quads, and viewports render their scenes as they go. */
  prepare(): void {
    if (this.#disposed) return;
    this.#dirty = false;
    // Ordinarily the tick has already stamped the clock for this frame and we
    // paint against that, so every control in the frame agrees on "now". A
    // direct call — a test, a manual paint, a desktop whose loop is stopped —
    // has nobody to stamp for it, so it stamps its own.
    if (!this.#timeSampled) this.#frameTime = performance.now() / 1000;
    this.#timeSampled = false;
    const previous = this.#paintTime;
    this.#paintTime = this.#frameTime;
    if (this.#fling) this.#stepFling(Math.min(0.05, this.#frameTime - previous));
    this.#syncBounds();
    // Before painting, so whatever a step sets is what this frame shows.
    for (const step of [...this.#frameSteps.keys()]) {
      if (this.#frameSteps.has(step)) this.#frameSteps.set(step, step(this.#frameTime));
    }
    // After layout and before painting: a field that has moved has to be over
    // the control it shadows before the browser is next asked to fill it.
    for (const form of this.#autofill) {
      if (form.disposed) this.#autofill.delete(form);
      else form.sync();
    }
    // Same slot, same reasoning, and never an `invalidate` of its own: the
    // desktop stops painting when it is clean, and one request for a frame
    // from here would pin it at sixty a second forever.
    this.#aria?.sync(this.#frameTime);

    const rc = this.rc;
    rc.begin();
    if (!this.overlay) {
      // Past every edge, so the quad's antialiased rim falls outside the canvas.
      const { width, height } = this.gpu.size;
      rc.fillRect({ x: -2, y: -2, width: width + 4, height: height + 4 }, this.#theme.palette.desktop);
    }
    // The one place the UI scale is applied. Everything the control tree does
    // above here is in UI units and knows nothing about it.
    const scaled = this.#uiScale !== 1;
    if (scaled) rc.pushTransform(0, 0, this.#uiScale);
    this.paint(rc, this.#theme);
    if (scaled) rc.popTransform();
  }

  draw(): void {
    if (!this.#disposed) this.rc.flush();
  }

  protected override onPaint(): void {
    // An overlay shows the game through; otherwise `prepare` has filled the background already.
  }

  /**
   * The drag ghost, over every window and every foreground child.
   *
   * Painted here rather than parented anywhere, for the reason `beginDrag`
   * gives: nothing that follows the pointer should take part in hit testing.
   */
  protected override onPaintOverlay(rc: RenderContext, theme: Theme): void {
    if (this.#snapPreview) theme.drawSnapPreview(rc, this.#snapPreview);
    const ghost = this.#drag?.ghost;
    if (!ghost || !ghost.visible) return;
    rc.pushTransform(ghost.x, ghost.y, 1);
    ghost.paint(rc, theme);
    rc.popTransform();
  }

  get stats(): { quads: number; draws: number; glyphs: number; atlasPages: number } {
    return {
      quads: this.batcher.instanceCount,
      draws: this.batcher.drawCallCount,
      glyphs: this.rc.atlas.glyphCount,
      atlasPages: this.rc.atlas.pageCount,
    };
  }

  dispose(): void {
    this.#disposed = true;
    this.tooltips.dispose();
    if (this.#ownsGame) this.stop();
    this.input.dispose();
    this.gamepads.dispose();
    this.#aria?.dispose();
    this.#aria = null;
    this.#navigationIndex = null;
    for (const form of this.#autofill) form.dispose();
    this.#autofill.clear();
    this.keyboard.dispose();
    for (const detach of this.#detach) detach();
    this.#detach.length = 0;
    this.rc.atlas.dispose();
    this.batcher.dispose();
    if (this.#ownsGame) this.game.dispose();
  }
}

/** Whether the user has asked their system for less movement. */
function prefersReducedMotion(): boolean {
  const query = globalThis.matchMedia;
  if (typeof query !== "function") return false;
  try {
    return query.call(globalThis, "(prefers-reduced-motion: reduce)").matches;
  } catch {
    // Not every host that has the function has a working media engine behind
    // it; a browser that cannot answer is not one that asked for less motion.
    return false;
  }
}

/** Keep the UI scale somewhere usable; 0 or a NaN would divide the app away. */
function clampScale(value: number): number {
  return Number.isFinite(value) ? Math.min(Math.max(value, 0.25), 4) : 1;
}
